import { createSelectorCreator, defaultMemoize as memoize } from 'reselect';
import { isEqual, isNullOrUndefined } from '@as-react/common-utils';
import SelectorUtil from './SelectorUtil';
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const defaultMemoize = memoize;
const productCodeIsEqual = (currentProduct, previousProduct) => currentProduct.productCode === previousProduct.productCode;
const productSelectedColorIdIsEqual = (currentProduct, previousProduct) => currentProduct.selectedColorId === previousProduct.selectedColorId;
const productSelectedSkuIsEqual = (currentProduct, previousProduct) => currentProduct.selectedSku === previousProduct.selectedSku;
const productColorAndSkuAreEqual = (currentProduct, previousProduct) => currentProduct.selectedColorId === previousProduct.selectedColorId &&
    currentProduct.selectedSku === previousProduct.selectedSku;
const selectedSkuDeliveryPromiseIsDifferent = (currentProduct, previousProduct) => currentProduct.deliveryPromise === previousProduct.deliveryPromise;
const selectedSkuDeliveryPromiseExtendedIsDifferent = (currentProduct, previousProduct) => currentProduct.deliveryPromiseExtended === previousProduct.deliveryPromiseExtended;
const productColorAndPriceAreEqual = (currentProduct, previousProduct) => {
    if (currentProduct.selectedColorId !== previousProduct.selectedColorId ||
        currentProduct.selectedSku !== previousProduct.selectedSku) {
        return false;
    }
    const currentColorVariation = SelectorUtil.getSelectedColor(currentProduct);
    const previousColorVariation = SelectorUtil.getSelectedColor(previousProduct);
    if (isNullOrUndefined(currentColorVariation) || isNullOrUndefined(previousColorVariation)) {
        return false;
    }
    if (currentProduct.selectedSku) {
        const currentColorVariationSKu = SelectorUtil.getSelectedSku(currentProduct, currentColorVariation);
        const previousColorVariationSKu = SelectorUtil.getSelectedSku(previousProduct, currentColorVariation);
        return isEqual(currentColorVariationSKu.prices, previousColorVariationSKu.prices);
    }
    return isEqual(currentColorVariation.prices, previousColorVariation.prices);
};
const productActiveSizesForSelectedColor = (currentProduct, previousProduct) => {
    if (currentProduct.selectedColorId !== previousProduct.selectedColorId) {
        return false;
    }
    const currentColorVariation = SelectorUtil.getSelectedColor(currentProduct);
    const previousColorVariation = SelectorUtil.getSelectedColor(previousProduct);
    if (isNullOrUndefined(currentColorVariation) || isNullOrUndefined(previousColorVariation)) {
        return false;
    }
    const activeCurrentSizes = currentColorVariation.sizes
        .filter(currentSize => currentSize.active)
        .map(currentSize => currentSize.code);
    const activePreviousSizes = previousColorVariation.sizes
        .filter(previousSize => previousSize.active)
        .map(previousSize => previousSize.code);
    return isEqual(activeCurrentSizes, activePreviousSizes);
};
const productContainsSameColorsInSameActiveAndVisibleState = (currentProduct, previousProduct) => {
    const currentProductVariation = currentProduct.productColorVariations;
    const previousProductVariation = previousProduct.productColorVariations;
    if (currentProductVariation.length !== previousProductVariation.length ||
        isNullOrUndefined(currentProductVariation) ||
        isNullOrUndefined(previousProductVariation)) {
        return false;
    }
    const currentPossibleColors = currentProductVariation.map(currentColorVariation => ({
        colorId: currentColorVariation.colorId,
        active: currentColorVariation.active,
        visible: currentColorVariation.visible,
        isAvailable: Number(currentColorVariation.availability) > 0,
    }));
    const previousPossibleColors = previousProductVariation.map(previousColorVariation => ({
        colorId: previousColorVariation.colorId,
        active: previousColorVariation.active,
        visible: previousColorVariation.visible,
        isAvailable: Number(previousColorVariation.availability) > 0,
    }));
    return isEqual(currentPossibleColors, previousPossibleColors);
};
export const productSelectorCreatorByProductCode = createSelectorCreator(defaultMemoize, productCodeIsEqual);
export const productSelectorCreatorBySelectedColorId = createSelectorCreator(defaultMemoize, productColorAndPriceAreEqual);
export const productSelectorCreatorByActiveSizedForSelectedColor = createSelectorCreator(defaultMemoize, productActiveSizesForSelectedColor);
export const customSelectedColorSelectorCreator = createSelectorCreator(defaultMemoize, productSelectedColorIdIsEqual);
export const customSelectedSkuSelectorCreator = createSelectorCreator(defaultMemoize, productSelectedSkuIsEqual);
export const customSelectedSkuDeliveryPromiseSelectorCreator = createSelectorCreator(defaultMemoize, selectedSkuDeliveryPromiseIsDifferent);
export const customSelectedSkuDeliveryPromiseExtendedSelectorCreator = createSelectorCreator(defaultMemoize, selectedSkuDeliveryPromiseExtendedIsDifferent);
export const makeGetAvailableColorsMemoizeSelectorCreator = createSelectorCreator(defaultMemoize, productContainsSameColorsInSameActiveAndVisibleState);
export const productSelectorCreatorByColorAndSku = createSelectorCreator(defaultMemoize, productColorAndSkuAreEqual);
